import $ from 'jquery';
import _ from 'underscore';
import { initAutosubmit } from 'util/autosubmit';
import { setAnalyticsEvents, track } from 'util/analytics';
import {
  $body,
  $window,
  jqGetElemById,
  isAndroid,
  isMobile,
  jqOffOn,
  setScrollPosition,
} from 'util/global';
import { keyboardNavigation } from 'util/ui';
import {
  EVT_QUIZ_POSITION_LOADING,
  EVT_QUIZ_POSITION_WILL_CHANGE,
  EVT_QUIZ_POSITION_DID_CHANGE,
  FUNC_NOOP,
  QUIZ_CHAPTER_INTRO_STATE,
} from 'util/constants';
import { initGenericControllers } from 'app/generic_controllers';
import { visualizationsPrefetchHandler } from 'app/visualizations';

const CONTENT_STATUS_NOT_LOADED = 'notloaded';
const CONTENT_STATUS_ERROR = 'error';

/** @module */
const preloaders = {};

function courseQuizEndstateIsVisible() {
  return jqGetElemById('course-quiz-endstate').is(':visible');
}

/**
 * Initial values of all global variables
 * @type {PreloaderData}
 */
const initialValues = {
  solvableList: [],
  currentPosition: null,
  $markers: null,
  inited: false,
  solvablesLoaded: 0,
  submitted: false,
  noHistory: false,
  enableKeyboardNav: false,
  targetPosition: null,
};

/**
 * @typedef {object} PreloaderData
 * @property {Array} solvableList - List of problem/pane data, format: {url, data, viewed, content}
 * @property {Number} currentPosition - Current position, 0-N
 * @property {JQuery} $markers - Position marker (status dot) elements
 * @property {boolean} inited - Flag to mark the first time this function is called
 * @property {Number} solvablesLoaded - Counter to determine how many problems have loaded
 * @property {boolean} submitted - Set when form is submitted to prevent popstates from happening
 *                                 until there's a response
 * @property {boolean} noHistory - Set if url should not get updated, e.g. chapter intros
 * @property {boolean} enableKeyboardNav - Set if user can use arrows keys to change problems
 * @property {Number} targetPosition - Position to switch to
 */

/** @type {PreloaderData}
 * this maintains the state of all solvables
 */
let quizPreloaderData = {};

/**
 * Default values for options object
 * @type {Object}
 */
const defaultOptions = {
  reloaded: false,
  instantResponse: false,
};

/** @type {Object.<string, boolean>} */
const cachedImageURLs = {};

/**
 * Preload an image that will be used later
 * @param {String} url
 */
function preloadImageForUrl(url) {
  if (!cachedImageURLs[url]) {
    const img = new Image();
    img.src = url;
    cachedImageURLs[url] = true;
  }
}

/**
 * List of handlers added by registerPreloadHandler
 * @type {Array.<Function>}
 */
const preloadHandlers = [];

/**
 * Add a preload handler for the current quiz. The passed-in function will be called with the
 * preloaded content when a quiz item is done preloading. This is useful for preloading any
 * images or visualizations within the content.
 * @param {Function} f
 */
function registerPreloadHandler(f) {
  preloadHandlers.push(f);
}

/**
 * Grab all of the image URLs from raw HTML text and preload the images
 * @param {String} textContent
 */
function preloadImagesForText(textContent) {
  const imageRegex = /img.*src="(.*?)"/g;
  const srcRegex = /src="(.*?)"/;
  const imageMatches = textContent.match(imageRegex);
  if (imageMatches) {
    imageMatches.forEach(m => {
      const srcMatches = m.match(srcRegex);
      if (srcMatches) {
        const url = srcMatches[1];
        preloadImageForUrl(url);
      }
    });
  }
}

// Add preload handlers
registerPreloadHandler(preloadImagesForText);
registerPreloadHandler(visualizationsPrefetchHandler);

/**
 * Practice preload controller. Preloads html for all problems in the set then manually
 * switches between problems updates url via pushState
 * @param {JQuery} $container
 * @param {object} opts
 * @param {bool} opts.reloaded - Flag for when this is called from a vkey function
 * @param {bool} opts.instantResponse - Flag for when this is called from an instant response
 */
preloaders.practice = function practice($container, optsArg) {
  const opts = Object.assign({}, defaultOptions, optsArg);
  const id = $container.data('id');

  if (opts.reset === true) {
    quizPreloaderData[id] = {
      solvableList: initialValues.solvableList.slice(0),
      currentPosition: initialValues.currentPosition,
      $markers: initialValues.$markers,
      inited: initialValues.inited,
      solvablesLoaded: initialValues.solvablesLoaded,
      submitted: initialValues.submitted,
      noHistory: initialValues.noHistory,
      targetPosition: initialValues.targetPosition,
    };
  } else {
    quizPreloaderData[id] =
      quizPreloaderData[id] ||
      Object.assign({}, initialValues, {
        solvableList: initialValues.solvableList.slice(0),
      });
  }

  // get current position
  quizPreloaderData[id].currentPosition =
    quizPreloaderData[id].currentPosition === null
      ? $container.data('current') - 1
      : quizPreloaderData[id].currentPosition;

  const containerData = $container.data();
  quizPreloaderData[id].noHistory = containerData.noHistory;
  quizPreloaderData[id].enableKeyboardNav = containerData.enableKeyboardNav === true;

  // initializing functions
  // --------------------------------------------------

  // preloading function - request the problem and save the response
  const preloadSolvableFromLink = $link => {
    const position = $link.data('position');
    $.get($link.attr('href'), {
      web_preloading: true,
      is_preloading: true,
      force_full_page: true,
    })
      .then(response => {
        quizPreloaderData[id].solvableList[position].content = response;
        // trigger an event when the problem is loaded for when the user tries to a
        // view a problem before it's loaded
        $container.trigger($.Event(`preloaded.p${position}`));
        quizPreloaderData[id].solvablesLoaded++;

        // call any preload handlers (e.g. preload image, preload visualizations)
        preloadHandlers.forEach(handler => handler(response));
      })
      .fail(() => {
        quizPreloaderData[id].solvableList[position].content = CONTENT_STATUS_ERROR;
        // trigger the `preloaded` event on error so that the content switcher can
        // handle it
        $container.trigger($.Event(`preloaded.p${position}`));
      });
  };

  // loop through position markers ("status dots") and save relevant info, trigger
  // preloading as necessary
  const setupSolvableList = () => {
    quizPreloaderData[id].$markers.each((position, element) => {
      const $this = $(element);
      const isCurrent = quizPreloaderData[id].currentPosition === position;
      const data = $this.data();

      quizPreloaderData[id].solvableList.push({
        url: $this.attr('href'),
        data,
        viewed: isCurrent,
        content: isCurrent ? $container.find('#solv-main') : CONTENT_STATUS_NOT_LOADED,
      });

      if (!isCurrent || data.isDummy) {
        _.defer(() => {
          preloadSolvableFromLink($this);
        });
      }
    });
  };

  // content switching
  // --------------------------------------------------

  // show a spinner and wait for the problem to load
  const contentSwitchCaseNotLoaded = position => {
    $body.trigger(EVT_QUIZ_POSITION_LOADING);
    $container.addClass('loading');
    // wait for the `preloaded` event before switching to the new problem
    $container.one(`preloaded.p${position}`, () => {
      // eslint-disable-next-line no-use-before-define
      switchToPosition(position);
      if (quizPreloaderData[id].solvableList[position].content !== CONTENT_STATUS_ERROR) {
        $container.removeClass('loading');
      }
    });
  };

  // on error, send the user to the problem's page
  const contentSwitchCaseError = position => {
    if (quizPreloaderData[id].noHistory) {
      window.location.reload();
    } else {
      window.location = quizPreloaderData[id].solvableList[position].url;
    }
  };

  // switch to the new problem, add events
  const contentSwitchCaseSuccess = (position, newContent, wasPopped) => {
    $body.trigger(EVT_QUIZ_POSITION_WILL_CHANGE);

    const $currentContent = $container.find('#solv-main').add(jqGetElemById('admin-sidebar'));
    const $newContent = $(newContent);

    // if this is an interstitial pane and the last incomplete item in the quiz
    const $paneContinueForm = $newContent.find('#interstitial-pane-continue-form');
    if (
      $paneContinueForm.length &&
      quizPreloaderData[id].$markers.filter(':not(.correct,.incorrect)').length === 1
    ) {
      $paneContinueForm.find('input[name="form_name"]').val('continue_and_finish');
      $paneContinueForm.find('button').html('Finish quiz');
    }
    const $endstateTriggeredButton = jqGetElemById('endstate-trigger-btn');
    if ($endstateTriggeredButton.length && $endstateTriggeredButton.data('show-immediately')) {
      // Don't nav! Stay here and let the endstate show up
      return;
    }

    // save the current state of the problem
    quizPreloaderData[id].solvableList[quizPreloaderData[id].currentPosition].content =
      $currentContent;

    // update the visible content
    quizPreloaderData[id].currentPosition = position;
    $currentContent.remove();
    $container.append($newContent);

    const isAttempted = $container.find('.endstate').length > 0;
    $body.toggleClass('endstate', isAttempted);

    // reattach events
    initGenericControllers($container, {
      force: true,
    });
    initAutosubmit($container);
    setAnalyticsEvents($container);
    // eslint-disable-next-line no-use-before-define
    bindPreloadEvents();

    // don't show the user the end of the problem
    setScrollPosition(0);

    // update the url unless this is coming from popstate
    if (!wasPopped) {
      // eslint-disable-next-line no-use-before-define
      navigateToPosition(quizPreloaderData[id].currentPosition);
    }

    // start the attempt if the problem has not been viewed, track sign_of_life
    const newPositionData =
      quizPreloaderData[id].solvableList[quizPreloaderData[id].currentPosition];
    if (!newPositionData.viewed) {
      quizPreloaderData[id].solvableList[quizPreloaderData[id].currentPosition].viewed = true;
      _.defer(
        (localNewPositionData, localCurrentPosition) => {
          const loc = window.location;
          const path = loc.pathname;

          // replicate full-page sign_of_life
          track('sign_of_life', {
            from_request: true,
            is_android: isAndroid,
            ajax: false,
            path,
            method: 'GET',
            full_path: path + loc.search,
          });

          switch (localNewPositionData.data.contentType) {
            case 'solvable':
              break;
            case 'interstitial_pane':
              break;
            default:
              throw Error('Unknown quiz item content type');
          }
        },
        newPositionData,
        quizPreloaderData[id].currentPosition,
      );
    }

    $body.trigger(EVT_QUIZ_POSITION_DID_CHANGE);
  };

  // handle the new position
  const switchToPosition = (position, wasPoppedArg) => {
    const wasPopped = wasPoppedArg === true;

    const $paneContinueForm = jqGetElemById('interstitial-pane-continue-form');
    if ($paneContinueForm.length) {
      const continuingDataKey = 'is-continuing';
      if ($paneContinueForm.data(continuingDataKey) !== true) {
        $paneContinueForm.data(continuingDataKey, true);
        quizPreloaderData[id].targetPosition = position;
        $paneContinueForm.submit();
      }

      const chapterIntroState = $body.data(QUIZ_CHAPTER_INTRO_STATE);
      if (chapterIntroState && chapterIntroState.active === true) {
        // Panes in chapter intros just need to keep track of the number of
        // currently-continuing panes
        $body.data(QUIZ_CHAPTER_INTRO_STATE, {
          active: true,
          numContinuingPanes: chapterIntroState.numContinuingPanes + 1,
        });
      } else {
        // All other panes need to wait for the component to refresh
        return;
      }
    }

    quizPreloaderData[id].targetPosition = null;

    if (
      position !== quizPreloaderData[id].currentPosition &&
      quizPreloaderData[id].solvableList[position]
    ) {
      const newContent = quizPreloaderData[id].solvableList[position].content;
      switch (newContent) {
        case CONTENT_STATUS_NOT_LOADED:
          contentSwitchCaseNotLoaded(position);
          break;
        case CONTENT_STATUS_ERROR:
          contentSwitchCaseError(position);
          break;
        default:
          contentSwitchCaseSuccess(position, newContent, wasPopped);
      }
    }
  };

  // update the url
  const navigateToPosition = (() => {
    if (window.history.pushState && !quizPreloaderData[id].noHistory) {
      return position => {
        _.defer(() => {
          const stateObj = {
            position,
          };
          window.history.pushState(stateObj, '', quizPreloaderData[id].solvableList[position].url);
        });
      };
    }
    return FUNC_NOOP;
  })();

  // event handling
  // --------------------------------------------------

  // What happens when clicking a link to view a new problem. This applies to position
  // markers as well as next/previous buttons.
  function linkHandler(e) {
    const $link = $(this);
    let position = $link.data('position');

    // Detect next and previous buttons if there's no `position`. If there's still no
    // position after this, `switchToPosition` will ignore it
    if (position === undefined) {
      if ($link.hasClass('btn-prev')) {
        const $noncompleteMarkers = quizPreloaderData[id].$markers.filter(
          ':not(.correct, .incorrect, .completed)',
        );
        if ($link.hasClass('solv-done-btn') && $noncompleteMarkers.length) {
          position = $noncompleteMarkers.first().data('position');
        } else {
          position = quizPreloaderData[id].currentPosition - 1;
        }
      } else {
        position = quizPreloaderData[id].currentPosition + 1;
      }
    }

    if (e.preventDefault) {
      e.preventDefault();
    }

    // prevents switching if the link is disabled
    if (!$link.hasClass('disabled')) {
      switchToPosition(position);
    }
  }

  // What happens when a popstate event fires
  const popstateHandler = () => {
    // if we're waiting for a response or it's the quiz endstate, don't let popstates happen
    if (quizPreloaderData[id].submitted || courseQuizEndstateIsVisible()) {
      window.history.forward();
    } else {
      let position;
      try {
        position = parseInt(window.location.href.match(/p=(\d)/)[1], 10) - 1;
      } catch (e) {
        const hasPosition = window.history.state && 'position' in window.history.state;
        position = hasPosition ? window.history.state.position : 0;
      }
      switchToPosition(position, true);
    }
  };

  // add events, manage position markers
  const bindPreloadEvents = () => {
    const getMarkersContainer = () => jqGetElemById(`prac-markers-${id}`);

    // Use the same position markers for every problem so that when the users finishes
    // a problem, that state is persistent for all problems in the set
    if (quizPreloaderData[id].$markers === null || opts.reloaded) {
      // when the problem is reloaded, save the markers in case the state has
      // been changed
      quizPreloaderData[id].$markers = getMarkersContainer().find('.prac-marker');
      // try getting current position again
      quizPreloaderData[id].currentPosition =
        quizPreloaderData[id].currentPosition < 0
          ? quizPreloaderData[id].$markers.filter('.current').data('position')
          : quizPreloaderData[id].currentPosition;

      if (isMobile && !opts.instantResponse) {
        // disable everything, we're waiting for the problem to reload at this
        // point for mobile
        const isDisabled =
          quizPreloaderData[id].solvablesLoaded < quizPreloaderData[id].solvableList.length - 1;
        _.defer(() => {
          $container
            .find('.btn-prev:not(.solv-done-btn)')
            .toggleClass('disabled', quizPreloaderData[id].currentPosition === 0);
          $container
            .find('.btn-next:not(.solv-done-btn)')
            .toggleClass(
              'disabled',
              quizPreloaderData[id].currentPosition ===
                quizPreloaderData[id].solvableList.length - 1,
            );
        });
        quizPreloaderData[id].$markers.toggleClass('disabled', isDisabled);
        $container.find('.actions-container').toggleClass('disabled', isDisabled);
      }
    } else {
      // update the current position
      quizPreloaderData[id].$markers.removeClass('current');
      quizPreloaderData[id].$markers.eq(quizPreloaderData[id].currentPosition).addClass('current');
      getMarkersContainer().empty().append(quizPreloaderData[id].$markers);
    }

    // attach link events
    jqOffOn(quizPreloaderData[id].$markers, 'click.quiznav', linkHandler);
    const $prevNextBtns = $container.find('.btn-next, .btn-prev').not('.btn-continue');
    jqOffOn($prevNextBtns, 'click.quiznav', linkHandler);

    // Wait for the response before trying to go to another problem. Otherwise, this
    // solvable's updated components will load in the new solvable.
    $container.find('#text_answer_entry_form,#mcq-form,#start-over-btn').on('submit', () => {
      quizPreloaderData[id].submitted = true;
      if (quizPreloaderData[id].solvablesLoaded !== quizPreloaderData[id].solvableList.length) {
        $container
          .find('.btn-next,.btn-prev')
          .add(quizPreloaderData[id].$markers)
          .addClass('disabled');
        $container.find('.actions-container').addClass('disabled');
      }
    });
  };

  const bindKeyboardEvents = () => {
    keyboardNavigation({
      eventKey: 'preloader',
      guard() {
        // ignore if viewing a modal, nav is disabled, or is quiz endstate
        return !(
          $body.hasClass('modal-open') ||
          quizPreloaderData[id].$markers.filter('.disabled').length > 0 ||
          courseQuizEndstateIsVisible()
        );
      },
      left() {
        switchToPosition(quizPreloaderData[id].currentPosition - 1);
      },
      right() {
        switchToPosition(quizPreloaderData[id].currentPosition + 1);
      },
    });
  };

  // initialization
  // --------------------------------------------------
  bindPreloadEvents();
  if (!quizPreloaderData[id].inited) {
    quizPreloaderData[id].inited = true;
    setupSolvableList();

    if (!quizPreloaderData[id].noHistory) {
      $window.on('popstate', popstateHandler);
    }

    if (quizPreloaderData[id].enableKeyboardNav) {
      bindKeyboardEvents();
    }
  }

  // reset the flags
  if (!opts.instantResponse) {
    quizPreloaderData[id].submitted = false;
  }
  opts.reloaded = false;
  opts.instantResponse = false;

  if (opts.useTargetPosition) {
    if (quizPreloaderData[id].targetPosition === null) {
      quizPreloaderData[id].targetPosition = quizPreloaderData[id].currentPosition + 1;
      if (quizPreloaderData[id].targetPosition < quizPreloaderData[id].solvableList.length) {
        switchToPosition(quizPreloaderData[id].targetPosition);
      }
    } else {
      switchToPosition(quizPreloaderData[id].targetPosition);
    }
    quizPreloaderData[id].targetPosition = null;
  }
};

/**
 * Convert a dummy preloader into a full one
 * @param {JQuery} $preloadContainer
 */
preloaders.convertDummyPreloader = ($preloadContainer, id) => {
  let content = CONTENT_STATUS_NOT_LOADED;
  const refreshPage = () => window.location.reload();

  try {
    [{ content }] = quizPreloaderData[id].solvableList;
  } catch (e) {
    content = CONTENT_STATUS_ERROR;
  }

  if (content === CONTENT_STATUS_NOT_LOADED || content === CONTENT_STATUS_ERROR) {
    refreshPage();
    return;
  }

  // replace current content with preloaded content
  const $content = $(content);
  $preloadContainer.empty().append($content);
  jqGetElemById('admin-sidebar').remove();

  // reset existing preload data
  $preloadContainer.data('enable-keyboard-nav', true);
  quizPreloaderData = {};

  // init everything
  preloaders.practice($preloadContainer);
  initAutosubmit($content);
  initGenericControllers($content);
  setAnalyticsEvents($content);
};

/**
 * @controller
 * Pre-loads background images of the container and all its children
 * @param {JQuery} $container
 */
preloaders.backgroundImages = $container => {
  /**
   * @param {HTMLElement} elem
   * @param {(':before'|':after')} [pseudoElem]
   */
  const preloadBackgroundImage = (elem, pseudoElem) => {
    const propValue = window.getComputedStyle(elem, pseudoElem).backgroundImage;
    if (propValue === 'none') return;

    const matchResult = propValue.match(/url\(['"]?(.*?)['"]?\)/);
    if (matchResult) {
      const [, url] = matchResult;
      preloadImageForUrl(url);
    }
  };

  $container
    .find('*')
    .add($container)
    .each((i, elem) => {
      preloadBackgroundImage(elem);
      preloadBackgroundImage(elem, ':before');
      preloadBackgroundImage(elem, ':after');
    });
};

module.exports = preloaders;
